import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import { FAQ_CATEGORIES } from 'src/consumer/constants'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { getDataWithoutParams, getFeatureItemBySlug } from 'src/consumer/services/featureServices'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

import ContactTeamModal from '../AumhumSite/ContactTeamModal'
import FloatingContactButtons from '../AumhumSite/FloatingContactButtons'
import { Footer, TopBar } from '../AumhumSite/Shell'
import { useContactNumber } from '../AumhumSite/useContactNumber'
import '../AumhumSite/AumhumSite.css'
import FaqSection from '../PrewallAndPaywall/FaqSection'
import { FAQ_LAYOUTS } from '../PrewallAndPaywall/faqComponents/utils'
import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import { LoadingSpinner } from '../Loaders'

import RequestSessionModal from './RequestSessionModal'
import {
  IconAward,
  IconCalendar,
  IconClock,
  IconGlobe,
  IconHeartPulse,
  IconShield,
  IconSparkle,
  IconStar,
  IconUsers,
} from './icons'
import './MentorLanding.css'

/*
 * MentorLandingPage
 *
 * Public profile for a single practitioner. Replaces the prior
 * thin wrapper around InfoAndSingleActionComponentVarOne +
 * EventListingUI. The redesign pulls the rich data already present
 * on the Practitioner / MentorResponse server model (credentials,
 * specializations, languages, testimonials, live session counts,
 * ratings) and presents it with visual hierarchy consistent with
 * the marketing site in AumhumSite.jsx.
 *
 * The "book a session" surface is deliberately framed as a
 * *request* — a curated call-back reviewed by the AUMHUM care
 * team. This communicates scarcity and clinical oversight, and
 * routes through the backend's WhatsApp + SQS flow rather than a
 * self-serve availability calendar.
 */

// ── helpers ────────────────────────────────────────────────────────

// Extract up to two initials from a display name to use as the
// portrait fallback when no photo URL resolves.
function getInitials(mentor) {
  const fallbackName =
    mentor?.name ||
    `${mentor?.firstName || ''} ${mentor?.lastName || ''}`.trim() ||
    ''
  if (!fallbackName) return '·'
  const parts = fallbackName.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
}

// Compact formatter for stat numbers: 12300 -> 12.3k, 1000000 -> 1M.
function formatCount(n) {
  const v = Number(n)
  if (!Number.isFinite(v) || v <= 0) return null
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
  return String(v)
}

// De-duplicate and strip falsy entries from a list of strings.
function uniqStrings(list) {
  const seen = new Set()
  const out = []
  for (const s of list || []) {
    if (!s) continue
    const key = String(s).trim()
    if (!key || seen.has(key.toLowerCase())) continue
    seen.add(key.toLowerCase())
    out.push(key)
  }
  return out
}

function buildCredentialLine(mentor) {
  const segments = []
  if (mentor?.showMyProfileAs) segments.push(mentor.showMyProfileAs)
  const roleCategories = mentor?.categories?.slice?.(0, 2) || []
  for (const c of roleCategories) {
    if (c && !segments.includes(c)) segments.push(c)
  }
  if (mentor?.instituteName) segments.push(mentor.instituteName)
  return uniqStrings(segments).join(' • ')
}

// Split LongDesc into paragraphs; accept either \n\n separators or a
// single blob. We keep it simple so the practitioner's formatting
// intent survives.
function toParagraphs(text) {
  if (!text) return []
  return String(text)
    .split(/\n{2,}|\r{2,}/g)
    .map((p) => p.trim())
    .filter(Boolean)
}

// Small IntersectionObserver wrapper to attach the `.is-in-view`
// reveal class once elements enter the viewport.
//
// NOTE: we take a `signal` (typically the loaded mentor data) so the
// effect re-runs once the async-loaded content has rendered. Without
// this, the first run would see zero `.ml-reveal` nodes (content not
// yet in the DOM) and they'd stay stuck at opacity:0 forever.
//
// Belt-and-braces: we also fall back to making everything visible if
// IntersectionObserver is missing OR never fires within 1.2s — so a
// broken observer can't hide the page.
function useRevealObserver(rootRef, signal) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const root = rootRef.current
    if (!root) return undefined
    const targets = Array.from(root.querySelectorAll('.ml-reveal'))
    if (targets.length === 0) return undefined

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in-view'))
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in-view')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.08, rootMargin: '0px 0px -5% 0px' },
    )
    targets.forEach((el) => observer.observe(el))

    // Safety net: if any target never intersects (e.g. always above the
    // fold with root scroll quirks, or the observer is broken by a
    // polyfill), force-reveal after a short delay. We only unhide —
    // never re-hide — so this is monotonic.
    const safetyTimer = window.setTimeout(() => {
      targets.forEach((el) => el.classList.add('is-in-view'))
    }, 1200)

    return () => {
      window.clearTimeout(safetyTimer)
      observer.disconnect()
    }
  }, [rootRef, signal])
}

// ── sub-sections ───────────────────────────────────────────────────

function Stars({ rating }) {
  const r = Math.max(0, Math.min(5, Number(rating) || 0))
  const full = Math.floor(r)
  const hasHalf = r - full >= 0.5
  return (
    <span className="ml-stars" aria-label={`Rating ${r.toFixed(1)} out of 5`}>
      {Array.from({ length: 5 }, (_, i) => {
        const filled = i < full || (i === full && hasHalf)
        return <IconStar key={i} filled={filled} />
      })}
    </span>
  )
}

function HeroSection({ mentor, onRequestSession }) {
  const portraitUrl = getImageLink(
    mentor?.profilePicId,
    mentor?.heroCardImage || mentor?.coverImage,
  )
  const credentialLine = buildCredentialLine(mentor)

  const specializations = uniqStrings([
    ...(mentor?.specializedCategories || []),
    ...(mentor?.categories || []),
  ]).slice(0, 6)

  const languages = uniqStrings(mentor?.languages || []).slice(0, 4)

  const ratingVal = Number(mentor?.rating || 0)
  const ratingLabel = ratingVal > 0 ? ratingVal.toFixed(1) : null
  const ratingCount = formatCount(mentor?.noOfRating)
  const sessionsCount = formatCount(mentor?.liveSessionsCount)
  const hrsCount = formatCount(mentor?.hrsTaught)
  const languagesCount = (mentor?.languages || []).length

  return (
    <section className="ml-hero">
      <div className="as-container">
        <div className="ml-hero-grid">
          <div className="ml-reveal">
            <span className="ml-availability-pill">
              <span className="ml-availability-dot" aria-hidden="true" />
              Accepting limited requests
            </span>
            <h1 className="ml-name">
              {mentor?.name ||
                `${mentor?.firstName || ''} ${mentor?.lastName || ''}`.trim() ||
                'Practitioner'}
            </h1>
            {credentialLine ? (
              <div className="ml-credential-line">{credentialLine}</div>
            ) : null}

            {mentor?.shortDesc ? (
              <p className="ml-short-desc">{mentor.shortDesc}</p>
            ) : null}

            {specializations.length > 0 ? (
              <div className="ml-chip-row">
                {specializations.map((c) => (
                  <span className="ml-chip" key={`spec-${c}`}>
                    <IconHeartPulse /> {c}
                  </span>
                ))}
              </div>
            ) : null}

            {languages.length > 0 ? (
              <div className="ml-chip-row" style={{ marginTop: '0.5rem' }}>
                {languages.map((l) => (
                  <span className="ml-chip ml-chip--green" key={`lang-${l}`}>
                    <IconGlobe /> {l}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="ml-stats">
              <div>
                <div className="ml-stat-value">
                  {ratingLabel ? (
                    <>
                      <IconStar /> {ratingLabel}
                    </>
                  ) : (
                    '—'
                  )}
                </div>
                <div className="ml-stat-label">
                  {ratingCount ? `${ratingCount} ratings` : 'Rating'}
                </div>
              </div>
              <div>
                <div className="ml-stat-value">{sessionsCount || '—'}</div>
                <div className="ml-stat-label">Live sessions</div>
              </div>
              <div>
                <div className="ml-stat-value">{hrsCount || '—'}</div>
                <div className="ml-stat-label">Hours of care</div>
              </div>
              <div>
                <div className="ml-stat-value">{languagesCount || '—'}</div>
                <div className="ml-stat-label">Languages</div>
              </div>
            </div>

            <div className="ml-cta-row">
              <button
                type="button"
                className="ml-cta-primary"
                onClick={onRequestSession}
              >
                <IconSparkle /> Request a session
              </button>
              <span className="ml-cta-sub">
                Reviewed by our care team · Typical response in hours
              </span>
            </div>
          </div>

          <div className="ml-reveal">
            <div className="ml-portrait">
              {portraitUrl ? (
                <img
                  src={portraitUrl}
                  alt={mentor?.name || 'Practitioner portrait'}
                  loading="eager"
                  decoding="async"
                />
              ) : (
                <div className="ml-portrait-fallback" aria-hidden="true">
                  {getInitials(mentor)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="ml-scarcity ml-reveal" role="note">
          <span className="ml-scarcity-icon">
            <IconShield />
          </span>
          <div>
            <div className="ml-scarcity-title">
              Not a self-serve calendar — and that's on purpose
            </div>
            <div className="ml-scarcity-body">
              {(mentor?.name || 'This practitioner') + "'"}s slots are curated by our
              care team. We review every request against clinical fit and availability
              so you see the right clinician at the right time — not just the next free
              slot. That's why booking is by request.
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function AboutSection({ mentor }) {
  const paragraphs = toParagraphs(mentor?.LongDesc || mentor?.longDesc || mentor?.description)
  if (paragraphs.length === 0) return null
  return (
    <section className="ml-section">
      <div className="as-container">
        <div className="ml-section-header ml-reveal">
          <div className="ml-section-eyebrow">About</div>
          <h2 className="ml-section-title">
            How {mentor?.firstName || 'they'} approach care
          </h2>
        </div>
        <div className="ml-prose ml-reveal">
          {paragraphs.map((p, i) => (
            <p key={`about-${i}`}>{p}</p>
          ))}
        </div>
      </div>
    </section>
  )
}

function CredentialsSection({ mentor }) {
  const registrations = (mentor?.registrationIds || []).filter(
    (r) => r && (r.number || r.registrationNumber || r.council),
  )
  const affiliations = mentor?.affiliations || []
  const hasContent =
    registrations.length > 0 || affiliations.length > 0 || mentor?.instituteName
  if (!hasContent) return null
  return (
    <section className="ml-section ml-section--alt">
      <div className="as-container">
        <div className="ml-section-header ml-reveal">
          <div className="ml-section-eyebrow">Credentials</div>
          <h2 className="ml-section-title">Verified & registered</h2>
          <p className="ml-section-sub">
            Every AUMHUM clinician is verified against their registering council and
            institution before joining the network.
          </p>
        </div>

        <div className="ml-cards ml-cards--3">
          {mentor?.instituteName ? (
            <div className="ml-card ml-reveal">
              <div className="ml-card-eyebrow">
                <IconAward style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: '-2px' }} /> Affiliation
              </div>
              <div className="ml-card-title">{mentor.instituteName}</div>
              <div className="ml-card-body">Primary clinical home.</div>
            </div>
          ) : null}

          {registrations.slice(0, 6).map((reg, i) => (
            <div className="ml-card ml-reveal" key={`reg-${i}`}>
              <div className="ml-card-eyebrow">
                <IconShield style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: '-2px' }} /> Registration
              </div>
              <div className="ml-card-title">
                {reg.council || reg.registrationCouncil || 'Registered clinician'}
              </div>
              <div className="ml-card-body">
                {reg.number || reg.registrationNumber
                  ? `#${reg.number || reg.registrationNumber}`
                  : 'Verified by AUMHUM'}
                {reg.country ? ` · ${reg.country}` : ''}
              </div>
            </div>
          ))}

          {affiliations.slice(0, 6).map((aff, i) => (
            <div className="ml-card ml-reveal" key={`aff-${i}`}>
              <div className="ml-card-eyebrow">
                <IconUsers style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: '-2px' }} /> Member of
              </div>
              <div className="ml-card-title">{aff?.name || aff?.title || 'Professional body'}</div>
              {aff?.sub || aff?.description ? (
                <div className="ml-card-body">{aff.sub || aff.description}</div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function TestimonialsSection({ mentor }) {
  const testimonials = (mentor?.testimonials || []).filter(
    (t) => t && (t.quote || t.text || t.content),
  )
  if (testimonials.length === 0) return null
  return (
    <section className="ml-section">
      <div className="as-container">
        <div className="ml-section-header ml-reveal">
          <div className="ml-section-eyebrow">What patients say</div>
          <h2 className="ml-section-title">Care that people come back to</h2>
        </div>

        <div className="ml-cards ml-cards--3">
          {testimonials.slice(0, 6).map((t, i) => (
            <div className="ml-testimonial ml-reveal" key={`tst-${i}`}>
              <div className="ml-testimonial-quote">
                {t.quote || t.text || t.content}
              </div>
              <div className="ml-testimonial-author">
                <div className="ml-testimonial-avatar" aria-hidden="true">
                  {(t.name || t.author || '·').trim().slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div className="ml-testimonial-name">
                    {t.name || t.author || 'Patient'}
                  </div>
                  {t.sub || t.role ? (
                    <div className="ml-testimonial-sub">{t.sub || t.role}</div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function HowItWorksSection() {
  return (
    <section className="ml-section ml-section--alt">
      <div className="as-container">
        <div className="ml-section-header ml-reveal">
          <div className="ml-section-eyebrow">How booking works</div>
          <h2 className="ml-section-title">Requests are curated — not queued</h2>
          <p className="ml-section-sub">
            Slots are reviewed the same day by our care team. You'll always hear back on
            WhatsApp with a confirmed time (or the best alternative).
          </p>
        </div>
        <div className="ml-cards ml-cards--3">
          <div className="ml-card ml-reveal">
            <div className="ml-card-eyebrow">
              <IconCalendar style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: '-2px' }} /> Step 1
            </div>
            <div className="ml-card-title">You send a request</div>
            <div className="ml-card-body">
              A short form — name, preferred windows, what's going on. Takes under a
              minute.
            </div>
          </div>
          <div className="ml-card ml-reveal">
            <div className="ml-card-eyebrow">
              <IconShield style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: '-2px' }} /> Step 2
            </div>
            <div className="ml-card-title">Care team reviews</div>
            <div className="ml-card-body">
              We check the practitioner's availability and clinical fit. If they're full,
              we suggest an equally qualified clinician in our network.
            </div>
          </div>
          <div className="ml-card ml-reveal">
            <div className="ml-card-eyebrow">
              <IconClock style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: '-2px' }} /> Step 3
            </div>
            <div className="ml-card-title">WhatsApp confirmation</div>
            <div className="ml-card-body">
              You get a WhatsApp message with your slot, prep notes, and a link to the
              session. Reply there to adjust anything.
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function FinalCTASection({ mentor, onRequestSession }) {
  return (
    <section className="ml-section">
      <div className="as-container">
        <div
          className="ml-reveal"
          style={{
            textAlign: 'center',
            padding: '3rem 1.5rem',
            borderRadius: '1.25rem',
            background:
              'linear-gradient(135deg, rgba(245,243,255,0.7) 0%, rgba(236,253,245,0.7) 100%)',
            border: '1px solid var(--violet-100)',
          }}
        >
          <h2
            className="ml-section-title"
            style={{ maxWidth: '32rem', margin: '0 auto' }}
          >
            Ready to see {mentor?.firstName || 'your clinician'}?
          </h2>
          <p
            className="ml-section-sub"
            style={{ maxWidth: '36rem', margin: '0.75rem auto 0' }}
          >
            Send a request — we'll come back with the next available slot, or the best
            alternative clinician if this one is fully booked.
          </p>
          <div
            style={{
              marginTop: '1.5rem',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              className="ml-cta-primary"
              onClick={onRequestSession}
            >
              <IconSparkle /> Request a session
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── top-level page ─────────────────────────────────────────────────

export default function MentorLandingPage() {
  const navigate = useNavigate()
  const { mentorSlug, mentorId } = useParams()
  const { profileData } = useContext(ProfileContext)
  // SetupContext isn't currently consumed but we keep the hook call so
  // context providers higher up that expect a subscriber (for lazy
  // setup fetches) still see this page.
  useContext(SetupContext)

  const [mentorData, setMentorData] = useState()
  const [dataLoading, setDataLoading] = useState(false)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [authVariant, setAuthVariant] = useState('login')
  const [requestOpen, setRequestOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const contact = useContactNumber()

  const rootRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    async function pageSetup() {
      setDataLoading(true)
      let response
      if (mentorSlug) {
        response = await getFeatureItemBySlug('mentor', mentorSlug, true)
      } else if (mentorId) {
        response = await getFeatureItemBySlug('mentor', mentorId, false)
      }
      if (cancelled) return
      if (
        response?.status === 200 &&
        response?.data &&
        Object.keys(response.data || {}).length !== 0 &&
        response.data?.data?.mentor
      ) {
        setMentorData(response.data.data.mentor)
      } else {
        navigate('/404')
      }
      setDataLoading(false)
    }
    pageSetup()
    // Keep the plans fetch around for parity with the old page — some
    // downstream widgets still read from it.
    getDataWithoutParams('/api/subscription-plan/1on1').catch(() => null)
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentorSlug, mentorId])

  // Pass mentorData as the signal so the observer re-attaches after
  // async content has rendered; otherwise `.ml-reveal` nodes would be
  // absent on first run and stay hidden forever.
  useRevealObserver(rootRef, mentorData)

  const mentorDisplayName = useMemo(
    () =>
      mentorData?.name ||
      `${mentorData?.firstName || ''} ${mentorData?.lastName || ''}`.trim() ||
      'Practitioner',
    [mentorData],
  )

  const openRequest = () => setRequestOpen(true)

  return (
    <>
      {mentorData && (
        <SEOComponent
          title={`${mentorDisplayName} | AUMHUM`}
          description={
            mentorData?.shortDesc
              ? `${mentorDisplayName} — ${mentorData.shortDesc}`
              : `Request a session with ${mentorDisplayName} — clinical mental healthcare on AUMHUM.`
          }
          image={getImageLink(mentorData?.profilePicId, mentorData?.coverImage)}
        />
      )}

      <ClubbedAuthModal
        open={showClubbedAuthModal}
        onClose={() => setShowClubbedAuthModal(false)}
        loginVariant={authVariant === 'login'}
      />

      {/*
        Wrapping in `.aumhum-site-root` lets the mentor page reuse the
        design tokens + sticky TopBar from the marketing site for a
        consistent look & feel.
      */}
      <div className="aumhum-site-root" ref={rootRef}>
        <div className="mentor-landing-root">
          <TopBar
            onLogin={() => {
              setAuthVariant('login')
              setShowClubbedAuthModal(true)
            }}
            contact={contact}
            onContactClick={() => setContactOpen(true)}
          />

          {dataLoading || !mentorData ? (
            <section className="ml-section">
              <div className="as-container">
                {dataLoading ? (
                  <div
                    className="ml-skeleton-hero"
                    aria-label="Loading practitioner details"
                  />
                ) : (
                  <LoadingSpinner bgColor="#F5F5F599" dark />
                )}
              </div>
            </section>
          ) : (
            <>
              <HeroSection mentor={mentorData} onRequestSession={openRequest} />
              <AboutSection mentor={mentorData} />
              <CredentialsSection mentor={mentorData} />
              <TestimonialsSection mentor={mentorData} />
              <HowItWorksSection />

              <section className="ml-section ml-section--alt" aria-label="Questions">
                <div className="as-container">
                  <div className="ml-section-header ml-reveal">
                    <div className="ml-section-eyebrow">Common questions</div>
                    <h2 className="ml-section-title">Before you book</h2>
                  </div>
                  <FaqSection
                    categoryIds={[FAQ_CATEGORIES.therapist_booking_page_faqs]}
                    layoutType={FAQ_LAYOUTS.TWO_COLUMN_SIDEBAR}
                  />
                </div>
              </section>

              <FinalCTASection mentor={mentorData} onRequestSession={openRequest} />

              <div className="ml-page-end-spacer" aria-hidden="true" />

              <div className="ml-mobile-cta-bar" role="region" aria-label="Request session">
                <span className="ml-mobile-cta-bar-label">{mentorDisplayName}</span>
                <button
                  type="button"
                  className="ml-cta-primary"
                  onClick={openRequest}
                >
                  <IconSparkle /> Request a session
                </button>
              </div>
            </>
          )}

          <Footer />

          {/*
            The modal lives INSIDE `.mentor-landing-root` because every
            style rule for it is namespaced under that class. Rendering
            it as a sibling (as it was originally) meant the modal did
            mount on "Request a session" but had zero CSS applied — no
            backdrop, no layout — so it looked like nothing happened.
          */}
          <RequestSessionModal
            open={requestOpen}
            onClose={() => setRequestOpen(false)}
            mentor={mentorData}
          />

          {/*
            Both the floating WhatsApp/phone pair and the "Connect with
            our team" dialog live inside `.mentor-landing-root` for the
            same reason as RequestSessionModal — their CSS is scoped
            to this wrapper.
          */}
          <FloatingContactButtons
            contact={contact}
            onPhoneClick={() => setContactOpen(true)}
          />
          <ContactTeamModal
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            contact={contact}
          />
        </div>
      </div>

      {/*
        Keep ProfileContext warm — this page can link into logged-in
        surfaces where the user's profile is expected to be present.
      */}
      {profileData ? null : null}
    </>
  )
}
